// @ts-strict-ignore
import type { Option } from '@usealloy/component-library';

import {
  DISPLAY_NAME_OPTION_KEY, SERVICE_FAILURE_DEFAULT_LABELS,
  SERVICE_FAILURE_DEFAULT_VALUES,
} from './constants';

import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import type { FormState, ServiceOption } from './ServiceOptionsTab/types';
import type { NodeEditorFormAdapter } from '../useNodeEditorForm';

import { buildIndexableActionOptionEntry } from '~/dashboard/views/workflows/store/engine/create/actions/helpers';
import { generatePublicId } from '~/dashboard/views/workflows/store/engine/utils';
import type { UseFormRegisterReturn } from 'react-hook-form';


export function createServiceOption({ key, value }: {
  key?: string;
  value?: string;
} = {}): ServiceOption {
  return {
    key: key ?? '',
    value: value ?? '',
    jsonValue: null,
    publicId: generatePublicId('actionoption'),
    serviceConfigOptionId: null,
    selectedValues: null,
  };
}

export function getServiceFailureDefaultOptions(): Option[] {
  return Object.values(SERVICE_FAILURE_DEFAULT_LABELS).map(label => ({
    label,
    value: label,
  }));
}

// form helpers:
export function buildServiceFailureDefaultOption(value: Action['serviceFailureDefault']): Option {
  switch (value) {
    case true:
      return {
        label: SERVICE_FAILURE_DEFAULT_LABELS.RUN_SERVICE,
        value: SERVICE_FAILURE_DEFAULT_LABELS.RUN_SERVICE,
      };
    case false:
      return {
        label: SERVICE_FAILURE_DEFAULT_LABELS.DO_NOT_RUN_SERVICE,
        value: SERVICE_FAILURE_DEFAULT_LABELS.DO_NOT_RUN_SERVICE,
      };
    case null:
      return {
        label: SERVICE_FAILURE_DEFAULT_LABELS.EXIT_WORKFLOW,
        value: SERVICE_FAILURE_DEFAULT_LABELS.EXIT_WORKFLOW,
      };
    default:
      return null;
  }
}

export function convertActionOptionsToServiceOptions(
  serviceOptions: Action['options'],
): ServiceOption[] {
  return Object.entries(serviceOptions).map(([key, value]) => ({
    ...value,
    key,
    value: value.value as string,
  }));
}

export function convertServiceOptionToActionOption(
  values: ServiceOption[],
): Action['options'] {
  if (!values) {
    return {};
  }
  const options: Action['options'] = {};
  values.forEach(({ key, ...rest }) => {
    options[key] = { ...rest };
  });
  return options;
}

export const getDisplayNameFromActionOptions = (
  options: Action['options'],
  serviceName: string,
): string => {
  if (!options[DISPLAY_NAME_OPTION_KEY]) {
    return serviceName;
  }
  return options[DISPLAY_NAME_OPTION_KEY].value as string;
};

export const convertDisplayNameToActionOption = (
  options: Action['options'],
  displayName: string,
  serviceName: Action['service']['name'],
): Action['options'] => {
  const isDisplayNameSameAsServiceName = displayName === serviceName;
  if (isDisplayNameSameAsServiceName || !displayName) return {};

  if (options[DISPLAY_NAME_OPTION_KEY]) {
    return buildIndexableActionOptionEntry(DISPLAY_NAME_OPTION_KEY, {
      ...options[DISPLAY_NAME_OPTION_KEY], value: displayName,
    });
  }

  return buildIndexableActionOptionEntry(DISPLAY_NAME_OPTION_KEY, { value: displayName });
};


/**
 * Builds an extended `register` object that extends the received `onChange` function
 * to include autosave
 */
export function buildAutoSavedRegister(args: {
  fieldMethods: UseFormRegisterReturn;
  handleAutoSave: (...a: any) => Promise<void>;
}) {
  const { fieldMethods, handleAutoSave } = args;

  const { onChange, ...rest } = fieldMethods;
  const displayNameRegisterWithAutoSave = {
    ...rest,
    onChange: e => {
      onChange(e);
      handleAutoSave();
    },
  };


  return displayNameRegisterWithAutoSave;
}


// form:
export const serviceOptionsFormAdapter: NodeEditorFormAdapter<FormState> = {
  getDefaultFormValues(action) {
    const { options, serviceFailureDefault, service } = action;
    return {
      additionalOptions: null, // handled within ServiceOptionsTab: requires ajax
      recurringWorkflow: null, // handled within ServiceOptionsTab: requires ajax
      serviceCredentials: null, // handled within ServiceOptionsTab: requires ajax
      serviceFailureDefaultOption: buildServiceFailureDefaultOption(serviceFailureDefault),
      displayName: getDisplayNameFromActionOptions(options, service.name),
    };
  },
  mapFormDataToAction(data, sourceAction) {
    const recurringWorkflowData = data.recurringWorkflow
      ? convertServiceOptionToActionOption([data.recurringWorkflow])
      : {};
    return {
      ...sourceAction,
      options: {
        ...convertServiceOptionToActionOption(data.additionalOptions),
        ...recurringWorkflowData,
        ...convertServiceOptionToActionOption(data.serviceCredentials),
        ...convertDisplayNameToActionOption(
          sourceAction.options, data.displayName, sourceAction.service.name,
        ),
      },
      serviceFailureDefault: SERVICE_FAILURE_DEFAULT_VALUES[
        data.serviceFailureDefaultOption.label
      ],
    };
  },
};
